import { createApi } from "@reduxjs/toolkit/query/react";
import { serializeError } from "serialize-error";
import { v4 as uuidv4 } from "uuid";
import { supabase, supabaseBaseQuery } from "../../../supabase";
import type { Judgement, JudgementScope, JudgementType, TextSelectionRange } from "../types";

interface GetJobsFeedbackArgs {
  jobIds: string[];
}

interface AddJudgementArgs {
  jobId: string;
  type: JudgementType;
  scope: JudgementScope;
  note?: string | null;
  selection?: TextSelectionRange | null;
  /** Used for optimistic author attribution. */
  author: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
  /** Cache key used by the parent getJobsFeedback query. */
  cacheJobIds: string[];
}

interface RemoveJudgementArgs {
  judgementId: string;
  jobId: string;
  cacheJobIds: string[];
}

function sortedJobIds(jobIds: string[]): string[] {
  return [...jobIds].sort();
}

function cacheArgs(jobIds: string[]): GetJobsFeedbackArgs {
  return { jobIds: sortedJobIds(jobIds) };
}

const judgementsApi = createApi({
  reducerPath: "api.Judgements",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["JobFeedback"],
  endpoints: (build) => ({
    getJobsFeedback: build.query<Judgement[], GetJobsFeedbackArgs>({
      queryFn: async ({ jobIds }) => {
        if (!jobIds.length) {
          return { data: [] };
        }

        const { data, error } = await supabase.rpc("get_jobs_feedback", {
          p_job_ids: jobIds,
        });

        if (error) return { error: serializeError(error) };

        return { data: (data ?? []) as unknown as Judgement[] };
      },
      serializeQueryArgs: ({ queryArgs }) => sortedJobIds(queryArgs.jobIds).join(","),
      providesTags: (_result, _error, { jobIds }) => [
        ...jobIds.map((id) => ({ type: "JobFeedback" as const, id })),
        { type: "JobFeedback", id: "LIST" },
      ],
    }),

    addJudgement: build.mutation<Judgement, AddJudgementArgs>({
      queryFn: async ({ jobId, type, scope, note, selection }) => {
        const { data, error } = await supabase.rpc("add_job_judgement", {
          p_job_id: jobId,
          p_type: type,
          p_scope: scope,
          p_note: note || undefined,
          p_selection_start: selection?.start,
          p_selection_end: selection?.end,
          p_quoted_text: selection?.quotedText,
        });

        if (error) return { error: serializeError(error) };

        return { data: data as unknown as Judgement };
      },
      onQueryStarted: async (
        { jobId, type, scope, note, selection, author, cacheJobIds },
        { dispatch, queryFulfilled },
      ) => {
        const optimistic: Judgement = {
          id: uuidv4(),
          job_id: jobId,
          type,
          scope,
          note: note?.trim() || null,
          selection_start: selection?.start ?? null,
          selection_end: selection?.end ?? null,
          quoted_text: selection?.quotedText ?? null,
          created_by: "user",
          user_id: author.id,
          author_full_name: author.full_name,
          author_email: author.email,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        const patch = dispatch(
          judgementsApi.util.updateQueryData("getJobsFeedback", cacheArgs(cacheJobIds), (draft) => {
            draft.push(optimistic);
          }),
        );

        try {
          const { data } = await queryFulfilled;
          dispatch(
            judgementsApi.util.updateQueryData(
              "getJobsFeedback",
              cacheArgs(cacheJobIds),
              (draft) => {
                const idx = draft.findIndex((j) => j.id === optimistic.id);
                if (idx !== -1) draft[idx] = data;
              },
            ),
          );
        } catch {
          patch.undo();
        }
      },
    }),

    removeJudgement: build.mutation<void, RemoveJudgementArgs>({
      queryFn: async ({ judgementId }) => {
        const { error } = await supabase.rpc("remove_job_judgement", {
          p_judgement_id: judgementId,
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      onQueryStarted: async ({ judgementId, cacheJobIds }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          judgementsApi.util.updateQueryData("getJobsFeedback", cacheArgs(cacheJobIds), (draft) => {
            const idx = draft.findIndex((j) => j.id === judgementId);
            if (idx !== -1) draft.splice(idx, 1);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

/**
 * Optimistically strip all feedback for a job from every cached getJobsFeedback
 * query. Used by re-run flows so the UI matches the DB clear trigger.
 */
function clearJobFeedbackFromCaches(
  dispatch: (action: unknown) => void,
  getState: () => Record<string, unknown>,
  jobId: string,
) {
  const state = getState();
  const apiState = state[judgementsApi.reducerPath] as
    | { queries?: Record<string, { endpointName?: string; originalArgs?: GetJobsFeedbackArgs }> }
    | undefined;
  const queries = apiState?.queries;

  if (!queries) return;

  for (const entry of Object.values(queries)) {
    if (entry?.endpointName !== "getJobsFeedback" || !entry.originalArgs) continue;

    dispatch(
      judgementsApi.util.updateQueryData("getJobsFeedback", entry.originalArgs, (draft) => {
        for (let i = draft.length - 1; i >= 0; i--) {
          if (draft[i].job_id === jobId) draft.splice(i, 1);
        }
      }),
    );
  }
}

const { useGetJobsFeedbackQuery, useAddJudgementMutation, useRemoveJudgementMutation } =
  judgementsApi;

export {
  judgementsApi,
  clearJobFeedbackFromCaches,
  useGetJobsFeedbackQuery,
  useAddJudgementMutation,
  useRemoveJudgementMutation,
  cacheArgs,
};
